import type { ChangeEvent, KeyboardEvent } from 'react';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { getActionAriaLabelSync } from '@/utils/ariaLabels';

import { useCheckout } from '@/hooks/useCheckout';
import { useToggle } from '@/hooks/useToggle';
import { useVoucher } from '@/hooks/useVoucher';
import { VOUCHER_TYPE } from '@/lib/const';
import { commonMessages } from '@/messages/common';
import { useRegionProvider } from '@/providers/RegionProvider';

import { Accordion } from '../Accordion';
import { Flex } from '../Flex';
import { Loader } from '../Loader';

import * as M from './messages';
import * as S from './Voucher.styles';

export const Voucher = ({
  isCheckoutStepTwo = true,
}: {
  isCheckoutStepTwo?: boolean;
}) => {
  const [state, setState] = useState({
    voucherCode: '',
    voucherInfo: '',
    inputValue: '',
    errorMessage: '',
    processing: false,
  });
  const updateState = (updates: Partial<typeof state>) => {
    if (updates.errorMessage === 'Promo code is invalid') {
      updates.errorMessage = formatMessage(commonMessages.promoCodeInvalid);
    }
    setState((prevState) => ({
      ...prevState,
      ...updates,
    }));
  };

  const {
    refresh,
    snapshot: {
      checkout: { discountName, id, voucherCode: voucher },
      voucherType,
    },
  } = useCheckout<true>();

  const { addVoucher, deleteVoucher } = useVoucher(isCheckoutStepTwo);

  const { formatMessage } = useIntl();
  const { locale } = useRegionProvider();

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = event.target.value.trim();
    updateState({ inputValue: sanitizedValue });
  };

  const handleEnterKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleVoucher();
    }
  };

  const handleVoucher = async () => {
    if (!state.inputValue) {
      updateState({
        errorMessage: formatMessage({
          id: 'checkout.shoppingBag.voucher.codeIsEmpty',
        }),
      });
      return;
    }
    try {
      const voucherCodeInput = state.inputValue.trim();
      /**
       * Hardcode to convert uppercase only for br25 All other codes are case-sensitive.
       */
      const voucherCodeToSubmit =
        voucherCodeInput.toUpperCase() === 'BR25' ? 'BR25' : voucherCodeInput;
      updateState({ processing: true, errorMessage: '' });
      const errorMessage = await addVoucher(id, voucherCodeToSubmit);

      if (errorMessage) {
        updateState({
          processing: false,
          errorMessage: errorMessage,
        });
        return;
      }
      await refresh(true);
      updateState({
        voucherCode: voucherCodeToSubmit,
        processing: false,
        inputValue: '', // Clear the input text
      });
    } catch (error) {
      if (error instanceof Error) {
        updateState({ processing: false, errorMessage: error.message });
      } else {
        updateState({
          processing: false,
          errorMessage: 'Unknown error occurred',
        });
      }
    }
  };

  const handleChange = async () => {
    try {
      updateState({ processing: true, errorMessage: '' });
      const errorMessage = await deleteVoucher(id);
      if (errorMessage) {
        updateState({
          processing: false,
          errorMessage,
        });
        return;
      }

      updateState({
        voucherCode: '',
        voucherInfo: '',
        inputValue: '',
        processing: false,
        errorMessage: '',
      });
      await refresh(true);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error?.message : 'Unknown error occurred';
      updateState({
        voucherCode: '',
        voucherInfo: '',
        inputValue: '',
        processing: false,
        errorMessage: errorMessage,
      });
    }
  };

  const handleSelect = () => {
    updateState({ processing: false, errorMessage: '' });
  };

  useEffect(() => {
    updateState({ voucherCode: voucher });
  }, [voucher]);

  const [isVoucherOpen, toggleVoucherSummary] = useToggle(true);

  const getVoucherCodeDisplay = (code: string, voucherType: string): string => {
    if (!code || !voucherType) return '';
    if (voucherType === VOUCHER_TYPE.PERCENTAGE) {
      return code.split('-')?.[0];
    } else {
      return code;
    }
  };

  return (
    <S.Voucher data-testid='Voucher'>
      <S.VoucherIconDesktop data-testid='VoucherIconDesktop' />
      <S.VoucherWrapper data-testid='VoucherWrapper'>
        <Accordion
          className='voucher-accordion'
          isOpen={isVoucherOpen}
          onClick={toggleVoucherSummary}
          title={
            <S.VoucherHeader data-testid='VoucherHeader'>
              <S.VoucherTitle data-testid='VoucherHeader'>
                {M.VoucherMessage}
              </S.VoucherTitle>
            </S.VoucherHeader>
          }
        >
          {state.voucherCode ? (
            <S.VoucherCodeWrapper data-testid='VoucherCodeWrapper'>
              <Flex justifyContent='space-between'>
                <S.VoucherCode data-testid='VoucherCode'>
                  {getVoucherCodeDisplay(state.voucherCode, voucherType)}
                </S.VoucherCode>

                <S.VoucherChange
                  aria-label={
                    getActionAriaLabelSync('change_voucher', locale.code) ||
                    'Change voucher code'
                  }
                  data-testid='VoucherChange'
                  onClick={handleChange}
                >
                  {state.processing ? (
                    <Loader height={20} noMargin size='xs' />
                  ) : (
                    formatMessage(commonMessages.change)
                  )}
                </S.VoucherChange>
              </Flex>
              {voucherType === VOUCHER_TYPE.FIXED && discountName && (
                <S.VoucherInfo color='secondaryGrey' data-testid='VoucherInfo'>
                  {discountName}
                </S.VoucherInfo>
              )}
              {voucherType === VOUCHER_TYPE.PERCENTAGE && (
                <Flex>
                  <S.VoucherInfo
                    color='success'
                    data-testid='VoucherSuccessInfo'
                  >
                    {formatMessage({
                      id: 'checkout.shoppingBag.voucher.codeApplied',
                    })}
                  </S.VoucherInfo>
                </Flex>
              )}
            </S.VoucherCodeWrapper>
          ) : (
            <S.VoucherInputWrapper>
              <S.VoucherInput
                data-testid='VoucherInput'
                onChange={handleInputChange}
                onKeyUp={handleEnterKey}
                onSelect={handleSelect}
                placeholder={formatMessage(commonMessages.enterCode)}
                value={state.inputValue}
              />
              <S.VoucherButton
                data-testid='VoucherButton'
                onClick={handleVoucher}
                variant='outlined'
              >
                {state.processing ? (
                  <Loader
                    data-testid='VoucherLoader'
                    height={20}
                    noMargin
                    size='xs'
                    width={38}
                  />
                ) : (
                  formatMessage(commonMessages.apply)
                )}
              </S.VoucherButton>
            </S.VoucherInputWrapper>
          )}
          {state.errorMessage && (
            <Flex mt={1}>
              <S.VoucherInfo color='error' data-testid='VoucherInfo'>
                {state.errorMessage}
              </S.VoucherInfo>
            </Flex>
          )}
        </Accordion>
      </S.VoucherWrapper>
    </S.Voucher>
  );
};
